/**
 * Where the websocket connects, and whether it is encrypted.
 *
 * A pure function in its own module, separate from `realtime.ts`, for two
 * reasons: that file touches `window` and instantiates Echo at import time, so
 * it cannot be loaded in a plain node test — and this decision is the one part
 * of the realtime setup worth pinning down, because getting it wrong is silent.
 */

export interface TransportInput {
  /** `window.location.protocol` — "https:" or "http:". */
  protocol: string
  /** `window.location.hostname`, used when no host is configured. */
  hostname: string
  /** VITE_REVERB_SCHEME. */
  scheme?: string
  /** VITE_REVERB_HOST. */
  host?: string
  /** VITE_REVERB_PORT. */
  port?: string
}

export interface Transport {
  host: string
  port: number
  useTLS: boolean
}

/**
 * **The page's own protocol wins.**
 *
 * A browser will not open a `ws://` socket from an `https://` document — it is
 * blocked as mixed content. So a build deployed over TLS with
 * `VITE_REVERB_SCHEME=http` would not leak anything; it would simply never
 * connect, the app would fall back to polling, and nothing on screen would say
 * why. Deriving TLS from the page instead of trusting the variable makes that
 * misconfiguration unrepresentable rather than merely documented.
 *
 * The reverse is allowed: an `http://` page may still be told to use `wss`, for
 * a TLS-terminating proxy in front of Reverb but not in front of the app.
 * Unusual, but not wrong, so it is not overridden.
 *
 * Port defaults follow the same logic. Behind a proxy the socket rides the
 * normal HTTPS port alongside everything else; only a direct-to-Reverb setup
 * needs 8080, which is the development case.
 */
export function resolveTransport(
  input: TransportInput,
  warn: (message: string) => void = console.error,
): Transport {
  const pageIsSecure = input.protocol === 'https:'
  const useTLS = pageIsSecure || input.scheme === 'https'

  if (pageIsSecure && input.scheme === 'http') {
    /*
     * Worth shouting about rather than silently correcting: the deployment
     * believes it is running plaintext, so the TLS route to Reverb may not
     * exist at all. Forcing wss is the only thing that *can* work from this
     * page, but the environment is wrong and someone has to fix it.
     */
    warn(
      '[KaProLean] VITE_REVERB_SCHEME=http on an https page. Forcing wss so the ' +
        'browser does not block the socket as mixed content — but fix the ' +
        'build config, and make sure Reverb is reachable over TLS.',
    )
  }

  return {
    host: input.host || input.hostname,
    port: Number(input.port ?? (useTLS ? 443 : 8080)),
    useTLS,
  }
}
